import * as React from 'react';
import Forbidden from 'components/Forbidden';
import { Redirect, Route, RouteProps } from 'react-router-dom';
import { checkAuthorization } from 'lib/AuthorizedLib';
import AppAuth from 'AppAuth';
import { useUserAuth } from 'hooks/useUserAuth';
import { useAuthInitialization } from 'hooks/useAuthInitialization';
import { useZywaveFeatures } from 'hooks/useZywaveFeatures';
import InternalError from 'components/InternalError';
import AppConfig from 'appConfig';
import { USER_TYPE_ZYWAVE } from 'lib/config/appConstants';

const RouteAuthenticated = ({ component: Component, path, ...rest }: RouteProps) => {
  // Only initialize auth query when AppAuth reports logged-in
  const { data: userInfo, isSuccess, isLoading, isError } = useUserAuth(AppAuth.loggedIn());
  const isZywave = isSuccess && userInfo?.account_type === USER_TYPE_ZYWAVE;
  useAuthInitialization(isZywave);

  // For Zywave users, ensure features are fetched/updated on every ProtectedRoute call
  // This ensures localStorage for 'zyw.feature.agencies' is always up to date
  useZywaveFeatures(userInfo?.id, userInfo?.account_type);

  if (isLoading) {
    return null;
  }
  // On auth error, show internal error
  if (isError) {
    return <InternalError />;
  }

  // Check if we're in an infinite redirect loop (only if feature is enabled)
  if (AppConfig.enableRedirectZywaveDotCom && AppAuth.isInRedirectLoop()) {
    return <InternalError />;
  }

  if (!AppAuth.loggedIn()) {
    // Check if redirect limit has been exceeded to detect potential loops (only if feature is enabled)
    if (AppConfig.enableRedirectZywaveDotCom) {
      const currentUrl = window.location.href;
      if (AppAuth.hasExceededRedirectLimit(currentUrl)) {
        // Max redirects exceeded, show error page
        return <InternalError />;
      }
    }
    return <Redirect to="/login" />;
  }

  // Clear redirect tracking on successful authentication (only if feature is enabled)
  if (AppConfig.enableRedirectZywaveDotCom) {
    AppAuth.clearRedirectTracking();
  }

  const authorizedObj = checkAuthorization();

  if (!authorizedObj.isAuthorized) {
    return <Forbidden homePathUri={authorizedObj.homePathUri} />;
  }

  // user is logged in and authorized to view the page
  return <Route component={Component} path={path} {...rest} />;
};

export default RouteAuthenticated;
